import React, { useState, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import arrowIcon from "@images/icons/go-button-icon.svg";

const Breakpoints = {
    Mobile: "768px",
    Tablet: "1024px",
  };

export const MediaQueries = {
    Mobile: `@media (max-width: ${Breakpoints.Mobile})`,
    Tablet: `@media (max-width: ${Breakpoints.Tablet})`,
};

const DosingContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  ${MediaQueries.Mobile} {
    padding: 48px 24px;
  }
`;

const DosingHeader = styled.div`
    display: grid;
    grid-template-columns: repeat(2, auto);
    justify-content: space-between;
    align-items: center;
    gap: 130px;

    ${MediaQueries.Mobile} {
        align-items: flex-start;
        justify-content: center;
        gap: 24px;
    }
`;

const DosingField = styled.div`
  position: relative;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, auto);
  align-items: center;
  gap: 24px;
  ${MediaQueries.Mobile} {
    grid-template-columns: repeat(2, auto);
    grid-template-rows: auto auto;
    grid-template-areas:
        "label input"
        ". toggle";
    gap: 0;
  }
`;

const Label = styled.label`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  font-size: 16px;
  color: #165F57;
  font-weight: 700;
  ${MediaQueries.Mobile} {
    grid-area: label;
    width: 90%;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 192px;
  ${MediaQueries.Mobile} {
    grid-area: input;
    width: 103px;
  }
`;

const Input = styled.input.attrs({
  type: 'text' // Add the type attribute directly
})`
  padding: 14px 24px !important; /* Adding !important to override */
  background-color: #FCFCFCB2 !important;
  border-radius: 48px !important;
  border: none !important;
  font-size: 16px !important;
  color: #165F57 !important;
  text-align: center !important;
  width: 192px !important;
  ${MediaQueries.Mobile} {
    width: 100% !important;
    padding: 8px 10px 23px 10px !important;
  }

  &::placeholder {
    color: #165F57 !important;
    font-size: 16px !important;
  }

  ${MediaQueries.Mobile} {
    &::placeholder {
      white-space: pre-line !important;
      content: attr(data-placeholder-mobile) !important;
    }
  }

  box-shadow: none !important;
  outline: none !important;
  appearance: none !important;
`;

const UnitToggle = styled.div.attrs({ className: 'unit-toggle' })`
  display: grid;
  grid-template-columns: repeat(3, auto);
  gap: 16px;
  align-items: center;
  justify-content: center;
  ${MediaQueries.Mobile} {
    grid-area: toggle;
    justify-content: center;
    margin-top: 16px;
  }
`;

const UnitButton = styled.button`
  padding: 0;
  font-size: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  color: #165F57;
  font-weight: 400;

  &.active {
    font-weight: 700;
  }

  &:focus {
    outline: none;
  }
`;

const Divider = styled.span`
  color: rgba(0, 0, 0, 0.3);
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const GoButton = styled.button.attrs({ className: 'calculate-button' })`
  display: flex;
  width: 130px;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 0.75rem 2rem;
  border-radius: 48px;
  font-size: 16px;
  font-weight: 700;
  color: #165F57;
  border: none;
  cursor: pointer;
  position: relative;
  isolation: isolate;
  background-color: #7AC5934D;

  ${MediaQueries.Mobile} {
    padding: 14px 24px;
    width: 95px;
  }

  &::before {
    content: "";
    position: absolute;
    inset: -1px;
    border-radius: 48px;
    padding: 1px;
    background: linear-gradient(116deg, #FFF 7%, rgba(92, 143, 137, 0.40) 10%, rgba(92, 143, 137, 0.40) 91%, #FFF 94%);
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: -1;
  }

  box-shadow: 0px 0px 50px 0px rgba(0, 0, 0, 0.05) inset, 0px 20px 50px -20px rgba(0, 0, 0, 0.10);
  backdrop-filter: blur(50px);

  &:hover::before {
    background: linear-gradient(68deg, rgba(255, 255, 255, 0.70) 7%, rgba(92, 143, 137, 0.60) 10%, rgba(92, 143, 137, 0.60) 91%, rgba(255, 255, 255, 0.70) 94%);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 24px;
  width: 100% !important;
  margin-top: 64px;
  padding: 50px 48px;
  background-color: white;
  border-radius: 48px;
  ${MediaQueries.Mobile} {
    margin: 32px auto 0 auto;
    padding: 36px 48px;
  }
`;

const ResultText = styled.p`
  font-size: 16px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.7);
`;

const DosageDisplay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DosageValue = styled.span`
  font-family: 'GT Walsheim', sans-serif;
  font-size: 40px;
  font-weight: 700;
  color: #165F57;
`;

/**
 * DosingCalculator - A component for calculating pediatric drug dosages based on weight
 *
 * @returns {React.ReactElement} Rendered component
 */
const DosingCalculator = ({ ...props }) => {
    const [weight, setWeight] = useState("");
    const [unit, setUnit] = useState("kg"); // kg or lbs
    const [calculatedDose, setCalculatedDose] = useState(null);
  const inputRef = useRef(null);

    // Formula for calculating dosage based on weight
    const calculateDosage = (weightValue, weightUnit) => {
        // Convert weight to kg if in lbs
        const weightInKg = weightUnit === "lbs" ? weightValue * 0.453592 : weightValue;
        // Calculate dosage: weight in kg × 4, up to a max of 266 mg
        const calculatedDose = weightInKg * 4;
        const dosage = Math.min(Math.round(calculatedDose), 266);

        return dosage;
    };

    const handleInputChange = (e) => {
        // Only allow numerical values and decimals
        const value = e.target.value;
        if (value === "" || /^\d*\.?\d*$/.test(value)) {
            setWeight(value);
        }
    };

    const handleUnitToggle = (selectedUnit) => {
        setUnit(selectedUnit);
    };

    const handleCalculate = () => {
      const weightValue = inputRef?.current?.value || weight;
      if (weightValue && !isNaN(parseFloat(weightValue))) {
        const dose = calculateDosage(parseFloat(weightValue), unit);
            setCalculatedDose(dose);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleCalculate();
        }
    };

    return (
		<DosingContainer className="max-w-default container-gradient-light !bg-soft_green rounded-all_48 py-[64px]">
			<div className="max-w-[714px] mx-auto">
            <DosingHeader>
                <DosingField>
                    <Label htmlFor="weight">Child's Weight:</Label>
                    <InputWrapper className="border-gradient-button">
                        <Input
                            id="weight"
                            type="text"
                            ref={inputRef}
                            value={weight}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyDown}
                            data-placeholder-mobile="Enter&#10;weight"
                            placeholder="Enter weight"
                        />
                    </InputWrapper>
                    <UnitToggle>
                        <UnitButton
                            active={unit === "kg"}
                            onClick={() => handleUnitToggle("kg")}
                            className={unit === "kg" ? "active" : ""}
                        >
                            kg
                        </UnitButton>
                        <Divider>|</Divider>
                        <UnitButton
                            active={unit === "lbs"}
                            onClick={() => handleUnitToggle("lbs")}
                            className={unit === "lbs" ? "active" : ""}
                        >
                            lbs
                        </UnitButton>
                    </UnitToggle>
                </DosingField>

                <ButtonWrapper>
                    <GoButton onClick={handleCalculate}>
                        Go
                        <img src={arrowIcon} alt="Arrow Icon" />
                    </GoButton>
                </ButtonWrapper>
            </DosingHeader>

            {calculatedDose !== null && (
                <ResultContainer className="border-gradient-light">
                    <ResultText>Based on your entry, the recommended dose for your pediatric patient is:</ResultText>
                    <DosageDisplay>
                        <DosageValue>
                            {calculatedDose} mg
                        </DosageValue>
                    </DosageDisplay>
					</ResultContainer>
				)}
			</div>
		</DosingContainer>
    );
};

// Simpler PropTypes definition
DosingCalculator.propTypes = {
    className: PropTypes.string
};

export default DosingCalculator;
